/**
 * Pumpkin, as every screen shows her. Use this rather than a bare <img> of
 * a pose so seasonal looks apply everywhere at once.
 *
 * Most of the year it renders exactly the <img> screens always had. In
 * Halloween season (October — src/lib/season.ts) the pose art swaps to her
 * pumpkin costume via the barrel, and her bat companion flies above her
 * head: a separate sprite with a flip-book flap and a gentle bob, so
 * Pumpkin herself stays the same size. On the curled-up resting pose the
 * bat is asleep too (wings wrapped, no flapping).
 *
 * Sizing contract: `className` goes on the outermost element either way —
 * in Halloween season a wrapper the <img> fills — so screen CSS that sizes
 * or animates Pumpkin (width, margins, pumpkin-idle, boot breathing) is
 * unchanged, and the bat moves with her.
 */
import type { ComponentProps, CSSProperties } from "react";

import { halloweenActive, pumpkin, type PumpkinPose } from "@/assets/pumpkin-images";
import {
  bat,
  batPlacement,
  isHalloweenPose,
  type BatPlacement,
} from "@/assets/pumpkin-images/halloween";
import "./PumpkinFigure.css";

type PumpkinFigureProps = Omit<ComponentProps<"img">, "src" | "alt"> & {
  pose: PumpkinPose;
  alt: string;
};

export function PumpkinFigure({ pose, className, draggable = false, ...img }: PumpkinFigureProps) {
  const placement = halloweenActive && isHalloweenPose(pose) ? batPlacement[pose] : undefined;

  if (!placement) {
    return <img className={className} src={pumpkin[pose]} draggable={draggable} {...img} />;
  }

  return (
    <span className={`pumpkin-figure ${className ?? ""}`}>
      <img className="pumpkin-figure__img" src={pumpkin[pose]} draggable={draggable} {...img} />
      <Bat placement={placement} />
    </span>
  );
}

function Bat({ placement }: { placement: BatPlacement }) {
  const style = {
    "--bat-x": `${placement.x}%`,
    "--bat-y": `${placement.y}%`,
    "--bat-size": `${placement.size}%`,
  } as CSSProperties;

  return (
    <span
      className={`pumpkin-bat ${placement.asleep ? "pumpkin-bat--asleep" : ""}`}
      style={style}
      aria-hidden="true"
    >
      {placement.asleep ? (
        <img className="pumpkin-bat__frame" src={bat.sleep} alt="" draggable={false} />
      ) : (
        <>
          <img
            className="pumpkin-bat__frame pumpkin-bat__frame--up"
            src={bat.up}
            alt=""
            draggable={false}
          />
          <img
            className="pumpkin-bat__frame pumpkin-bat__frame--down"
            src={bat.down}
            alt=""
            draggable={false}
          />
        </>
      )}
    </span>
  );
}
